import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  message,
  Row,
  Col,
  Divider
} from 'antd';
import { PlusOutlined, CloseCircleOutlined } from '@ant-design/icons';
import quotationItemTemplateService, {
  QuotationItemTemplate,
  CreateQuotationItemTemplateInput
} from '../../../../services/quotationItemTemplateService';
import apiService from '../../../../services/apiService';
import { useTranslation } from "react-i18next";

const { Option } = Select;

// Predefined categories for quotation item templates.
// `value` is the persisted `category` and is compared below — never translate it.
// `label` is the i18n key, translated at render via t(cat.label).
const TEMPLATE_CATEGORIES = [
  { value: 'Main Item', label: 'Main Item' },
  { value: 'Options', label: 'Options' }
];

// Built-in subcategory + unit suggestions. Anything the user adds via the
// "+" affordance is persisted to accounting_settings (subcategoryOptions /
// unitOptions) so it appears next time and for the whole company.
const DEFAULT_SUBCATEGORY_OPTIONS = ['Extra Storage', 'Value-added Service'];
const DEFAULT_UNIT_OPTIONS = ['unit(s)', 'Rack(s)', 'Store', 'Month(s)', 'Order(s)', 'Hour(s)'];

const mergeOptions = (defaults: string[], persisted: string[] | undefined | null): string[] => {
  const seen = new Set(defaults);
  const extras = (persisted || []).filter(v => !!v && !seen.has(v));
  return [...defaults, ...extras];
};

interface QuotationItemTemplateFormProps {
  visible: boolean;
  template?: QuotationItemTemplate | null;
  onClose: () => void;
  onSuccess: () => void;
}

const QuotationItemTemplateForm: React.FC<QuotationItemTemplateFormProps> = ({
  visible,
  template,
  onClose,
  onSuccess
}) => {
    const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('Main Item');

  // Persistent dropdown options pulled from accounting_settings. Users can
  // add new subcategory / unit values via the "+" affordance on each dropdown.
  const [accountingSettings, setAccountingSettings] = useState<any>(null);
  const [subcategoryOptions, setSubcategoryOptions] = useState<string[]>(DEFAULT_SUBCATEGORY_OPTIONS);
  const [unitOptions, setUnitOptions] = useState<string[]>(DEFAULT_UNIT_OPTIONS);
  const [newSubcategoryInput, setNewSubcategoryInput] = useState<string>('');
  const [newUnitInput, setNewUnitInput] = useState<string>('');

  useEffect(() => {
    if (visible) {
      // Load latest options from settings whenever the modal opens
      apiService.get('/accounting/settings').then((response: any) => {
        const s = response.data || null;
        setAccountingSettings(s);
        setSubcategoryOptions(mergeOptions(DEFAULT_SUBCATEGORY_OPTIONS, s?.subcategoryOptions));
        setUnitOptions(mergeOptions(DEFAULT_UNIT_OPTIONS, s?.unitOptions));
      }).catch(err => console.error('Failed to load accounting settings:', err));

      if (template) {
        // Edit mode
        const category = template.category || 'Main Item';
        setSelectedCategory(category);
        form.setFieldsValue({
          itemCode: template.itemCode,
          description: template.description,
          category: category,
          subcategory: template.subcategory || undefined,
          unit: template.unit || 'unit(s)',
          unitPrice: Number(template.unitPrice),
          taxRate: template.taxRate || 0,
          discountType: template.discountType || 'fixed',
          discount: template.discount || 0
        });
      } else {
        // Create mode
        setSelectedCategory('Main Item');
        form.resetFields();
        form.setFieldsValue({
          category: 'Main Item',
          unit: 'unit(s)',
          unitPrice: 0,
          taxRate: 0,
          discountType: 'fixed',
          discount: 0
        });
      }
    }
  }, [visible, template, form]);

  // Persist a newly-typed option (subcategory / unit) to accounting_settings so
  // it survives reload and appears for everyone in the company. Idempotent.
  const persistCompanyOption = async (kind: 'subcategoryOptions' | 'unitOptions', value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    const current: string[] = (accountingSettings?.[kind] as string[]) || [];
    if (current.includes(trimmed)) return;
    const updated = [...current, trimmed];
    try {
      await apiService.put('/accounting/settings', { [kind]: updated });
      setAccountingSettings({ ...(accountingSettings || {}), [kind]: updated });
    } catch (err) {
      console.error(`Failed to persist ${kind}:`, err);
    }
  };

  // Counterpart to persistCompanyOption — remove a user-added option from
  // both local state and company settings. Defaults stay (× is hidden for them).
  const removeCompanyOption = async (
    kind: 'subcategoryOptions' | 'unitOptions',
    value: string,
    setter: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setter(prev => prev.filter(v => v !== trimmed));
    const current: string[] = (accountingSettings?.[kind] as string[]) || [];
    const updated = current.filter(v => v !== trimmed);
    try {
      await apiService.put('/accounting/settings', { [kind]: updated });
      setAccountingSettings({ ...(accountingSettings || {}), [kind]: updated });
    } catch (err) {
      console.error(`Failed to remove ${kind} option:`, err);
    }
  };

  // Render <Option> content with a small × button on user-added values so
  // they can be deleted from the company settings.
  const renderOptionContent = (
    value: string,
    defaults: string[],
    onRemove: (v: string) => void
  ) => {
    const isDefault = defaults.includes(value);
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <span>{value}</span>
        {!isDefault && (
          <CloseCircleOutlined
            style={{ color: '#bbb', fontSize: 12, cursor: 'pointer' }}
            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemove(value); }}
          />
        )}
      </div>
    );
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Subcategory is now optional and applies regardless of category — the
      // previous gate ("only save subcategory when category === 'Options'")
      // silently dropped user input for Main Items, which is what the user
      // reported as "subcategory not able to save".
      const data: CreateQuotationItemTemplateInput = {
        itemCode: values.itemCode,
        description: values.description,
        category: values.category || 'Main Item',
        subcategory: values.subcategory || undefined,
        unit: values.unit || undefined,
        unitPrice: values.unitPrice || 0,
        taxRate: values.taxRate || 0,
        discountType: values.discountType || 'fixed',
        discount: values.discount || 0
      };

      if (template) {
        // Update existing template
        await quotationItemTemplateService.updateTemplate(template.id, data);
        message.success(t('Quotation item template updated successfully'));
      } else {
        // Create new template
        await quotationItemTemplateService.createTemplate(data);
        message.success(t('Quotation item template created successfully'));
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      message.error(t('Failed to save template: {{message}}', { message: error.message }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={template ? t('Edit Quotation Item Template') : t('Create Quotation Item Template')}
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t('Cancel')}
                      </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
          {template ? t('Update') : t('Create')}
        </Button>
      ]}
      width={700}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          unitPrice: 0,
          taxRate: 0,
          discountType: 'fixed',
          discount: 0
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="itemCode"
              label={t('Item Title')}
              rules={[
                { required: true, message: t('Please enter an item title') }
              ]}
            >
              <Input placeholder="e.g., Consulting Service, Product Package" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="category"
              label={t('Category')}
              rules={[{ required: true, message: t('Please select a category') }]}
              tooltip={t('Main Item: Regular quotation items. Options: Optional services shown on a separate page.')}
            >
              <Select
                placeholder={t('Select a category')}
                onChange={(value) => {
                  setSelectedCategory(value);
                  if (value !== 'Options') {
                    form.setFieldValue('subcategory', undefined);
                  }
                }}
              >
                {TEMPLATE_CATEGORIES.map((cat) => (
                  <Option key={cat.value} value={cat.value}>
                    {t(cat.label)}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="subcategory"
              label={t('Subcategory')}
              tooltip={t('Optional. Subcategory groups items / options under a header in the PDF (e.g., Extra Storage, Value-added Service). Applies to both Main Items and Options.')}
            >
              <Select
                allowClear
                placeholder={t('Select or add a subcategory')}
                optionLabelProp="label"
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    <Divider style={{ margin: '4px 0' }} />
                    <div style={{ display: 'flex', gap: 4, padding: '4px 8px' }}>
                      <Input
                        size="small"
                        placeholder={t('New subcategory')}
                        value={newSubcategoryInput}
                        onChange={(e) => setNewSubcategoryInput(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        style={{ flex: 1 }}
                      />
                      <Button
                        size="small"
                        type="text"
                        icon={<PlusOutlined />}
                        onClick={() => {
                          const trimmed = newSubcategoryInput.trim();
                          if (trimmed && !subcategoryOptions.includes(trimmed)) {
                            setSubcategoryOptions([...subcategoryOptions, trimmed]);
                            form.setFieldValue('subcategory', trimmed);
                            setNewSubcategoryInput('');
                            persistCompanyOption('subcategoryOptions', trimmed);
                          }
                        }}
                      />
                    </div>
                  </>
                )}
              >
                {subcategoryOptions.map(s => (
                  <Option key={s} value={s} label={s}>
                    {renderOptionContent(s, DEFAULT_SUBCATEGORY_OPTIONS, v => removeCompanyOption('subcategoryOptions', v, setSubcategoryOptions))}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="unit"
              label={t('Default Unit')}
              tooltip={t("Used as the default unit when this template is applied to a quotation line. Defaults to 'unit(s)'.")}
            >
              <Select
                placeholder={t('Select a unit')}
                optionLabelProp="label"
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    <Divider style={{ margin: '4px 0' }} />
                    <div style={{ display: 'flex', gap: 4, padding: '4px 8px' }}>
                      <Input
                        size="small"
                        placeholder={t('New unit')}
                        value={newUnitInput}
                        onChange={(e) => setNewUnitInput(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        style={{ flex: 1 }}
                      />
                      <Button
                        size="small"
                        type="text"
                        icon={<PlusOutlined />}
                        onClick={() => {
                          const trimmed = newUnitInput.trim();
                          if (trimmed && !unitOptions.includes(trimmed)) {
                            setUnitOptions([...unitOptions, trimmed]);
                            form.setFieldValue('unit', trimmed);
                            setNewUnitInput('');
                            persistCompanyOption('unitOptions', trimmed);
                          }
                        }}
                      />
                    </div>
                  </>
                )}
              >
                {unitOptions.map(u => (
                  <Option key={u} value={u} label={u}>
                    {renderOptionContent(u, DEFAULT_UNIT_OPTIONS, v => removeCompanyOption('unitOptions', v, setUnitOptions))}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="description"
          label={t('Description')}
          rules={[{ required: true, message: t('Please enter a description') }]}
        >
          <Input.TextArea
            rows={3}
            placeholder="e.g., Professional Consulting Service - Per Hour"
          />
        </Form.Item>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="unitPrice"
              label={t('Default Unit Price')}
              rules={[{ required: true, message: t('Please enter a unit price') }]}
            >
              <InputNumber
                min={0}
                precision={2}
                prefix="$"
                style={{ width: '100%' }}
                placeholder="0.00"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="taxRate"
              label={t('Default Tax Rate (%)')}
            >
              <InputNumber
                min={0}
                max={100}
                precision={2}
                style={{ width: '100%' }}
                placeholder="0"
                formatter={(value) => `${value}%`}
                parser={(value) => parseFloat(value!.replace('%', '')) || 0}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label={t('Default Discount')}>
              <Input.Group compact>
                <Form.Item name="discount" noStyle>
                  <InputNumber
                    min={0}
                    style={{ width: '60%' }}
                    placeholder="0"
                  />
                </Form.Item>
                <Form.Item name="discountType" noStyle>
                  <Select style={{ width: '40%' }}>
                    <Option value="fixed">$</Option>
                    <Option value="percentage">%</Option>
                  </Select>
                </Form.Item>
              </Input.Group>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default QuotationItemTemplateForm;